import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ClearAll as ClearAllIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import globalErrorHandler from '../utils/GlobalErrorHandler';

const useStyles = makeStyles((theme) => ({
  debugPanel: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    width: '400px',
    maxHeight: '500px',
    zIndex: 9999,
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.warning.main}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[8],
  },
  header: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
  },
  headerIcon: {
    marginRight: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(2),
    maxHeight: '400px',
    overflow: 'auto',
  },
  errorItem: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  errorHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  errorDetails: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '200px',
    overflow: 'auto',
  },
  noErrors: {
    textAlign: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const ErrorDebugPanel = () => {
  const classes = useStyles();
  const [errors, setErrors] = useState([]);
  const [expandedErrors, setExpandedErrors] = useState(new Set());
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    loadErrors();
    
    // Refresh errors every 5 seconds
    const interval = setInterval(loadErrors, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadErrors = () => {
    const storedErrors = globalErrorHandler.getStoredErrors();
    setErrors(storedErrors);
  };

  const toggleErrorExpansion = (index) => {
    const newExpanded = new Set(expandedErrors);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedErrors(newExpanded);
  };

  const deleteError = (index) => {
    const newErrors = [...errors];
    newErrors.splice(index, 1);
    
    // Update localStorage
    try {
      localStorage.setItem('app_errors', JSON.stringify(newErrors));
      setErrors(newErrors);
    } catch (e) {
      console.warn('Could not delete error:', e);
    }
  };

  const clearAllErrors = () => {
    globalErrorHandler.clearStoredErrors();
    setErrors([]);
    setExpandedErrors(new Set());
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  // Only show in development mode
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <>
      {/* Toggle button */}
      <div
        style={{
          position: 'fixed',
          bottom: '16px',
          right: '16px',
          zIndex: 9998
        }}
      >
        <Button
          variant="contained"
          color="warning"
          startIcon={<BugReportIcon />}
          onClick={toggleVisibility}
          style={{ minWidth: 'auto', padding: '8px' }}
        >
          {errors.length > 0 ? `${errors.length} Errors` : 'Debug'}
        </Button>
      </div>

      {/* Debug Panel */}
      {isVisible && (
        <Paper className={classes.debugPanel}>
          <div className={classes.header}>
            <div className={classes.headerTitle}>
              <BugReportIcon className={classes.headerIcon} />
              <Typography variant="subtitle2">
                Error Debug Panel
              </Typography>
            </div>
            <IconButton
              size="small"
              onClick={toggleVisibility}
              style={{ color: 'inherit' }}
            >
              <ExpandLessIcon />
            </IconButton>
          </div>

          <div className={classes.content}>
            {errors.length === 0 ? (
              <div className={classes.noErrors}>
                <Typography variant="body2">
                  No errors logged yet
                </Typography>
              </div>
            ) : (
              <List dense>
                {errors.map((error, index) => (
                  <div key={index} className={classes.errorItem}>
                    <ListItem
                      button
                      onClick={() => toggleErrorExpansion(index)}
                      className={classes.errorHeader}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            {error.type}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" noWrap>
                            {new Date(error.timestamp).toLocaleTimeString()} - {error.message?.substring(0, 50)}...
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteError(index);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {expandedErrors.has(index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <Collapse in={expandedErrors.has(index)}>
                      <Divider />
                      <div className={classes.errorDetails}>
                        <Typography variant="caption" component="div">
                          <strong>Message:</strong> {error.message}
                        </Typography>
                        {error.stack && (
                          <Typography variant="caption" component="div" style={{ marginTop: '8px' }}>
                            <strong>Stack:</strong>
                            <pre style={{ margin: '4px 0', fontSize: '0.7rem' }}>
                              {error.stack}
                            </pre>
                          </Typography>
                        )}
                        <Typography variant="caption" component="div" style={{ marginTop: '8px' }}>
                          <strong>URL:</strong> {error.url}
                        </Typography>
                      </div>
                    </Collapse>
                  </div>
                ))}
              </List>
            )}

            <div className={classes.actions}>
              <Typography variant="caption" color="textSecondary">
                {errors.length} error(s) logged
              </Typography>
              <Button
                size="small"
                startIcon={<ClearAllIcon />}
                onClick={clearAllErrors}
                disabled={errors.length === 0}
              >
                Clear All
              </Button>
            </div>
          </div>
        </Paper>
      )}
    </>
  );
};

export default ErrorDebugPanel;
